import { List } from "immutable";
import _ from "lodash";

import {
  IDENTIFIER_LINE_HEIGHT,
  ITEM_CARD_IDENTIFIER_MARGIN_X,
  ITEM_CARD_IDENTIFIER_MARGIN_Y,
  ITEM_CARD_INLINE_AVATAR_MARGIN_X,
  ITEM_CARD_INLINE_AVATAR_MARGIN_Y,
  ITEM_CARD_INLINE_AVATAR_SIZE,
  TABLE_CELL_AVATAR_MARGIN_BOTTOM,
  TABLE_CELL_AVATAR_MARGIN_LEFT,
  TABLE_CELL_AVATAR_MARGIN_TOP,
  TABLE_IDENTIFIER_SLUG_MARGIN_BOTTOM,
  TABLE_IDENTIFIER_SLUG_MARGIN_TOP,
  TABLE_IDENTIFIER_SLUG_MARGIN_X,
  TABLE_IDENTIFIER_SLUG_MIN_WIDTH
} from "../constants";
import {
  AVERAGE_CHAR_PIXEL_WIDTH,
  LINE_HEIGHT_RATIO,
  // SPACE_CHAR_PIXEL_WIDTH,
  TEXT_TITLE_PADDING
} from "../constants/characters";
import Dimensions, { scalePixelsToFontSize } from "../constants/styles/dimensions";
import { createFromJS } from "../types";

const isCharPrintable = char => !/[\x00-\x1F]/.test(char);

export const calcIdentifierSize = (identifier, isAvatar, fontSize, widthMap) => {
  if (isAvatar) {
    const SCALED_INLINE_AVATAR_SIZE = scalePixelsToFontSize(ITEM_CARD_INLINE_AVATAR_SIZE, fontSize);
    const SCALED_INLINE_AVATAR_MARGIN_Y = scalePixelsToFontSize(ITEM_CARD_INLINE_AVATAR_MARGIN_Y, fontSize);
    const SCALED_INLINE_AVATAR_MARGIN_X = scalePixelsToFontSize(ITEM_CARD_INLINE_AVATAR_MARGIN_X, fontSize);
    return {
      width: SCALED_INLINE_AVATAR_SIZE + SCALED_INLINE_AVATAR_MARGIN_X,
      height: SCALED_INLINE_AVATAR_SIZE + SCALED_INLINE_AVATAR_MARGIN_Y * 2
    };
  } else {
    const idFontSize = scalePixelsToFontSize(Dimensions.IDENTIFIER.FONT_SIZE, fontSize, true);
    const SCALED_INLINE_IDENTIFIER_PADDING_X = scalePixelsToFontSize(Dimensions.IDENTIFIER.PADDING.LEFT, idFontSize);
    const SCALED_INLINE_IDENTIFIER_PADDING_TOP = scalePixelsToFontSize(Dimensions.IDENTIFIER.PADDING.TOP, idFontSize);
    const SCALED_INLINE_IDENTIFIER_PADDING_BOTTOM = scalePixelsToFontSize(
      Dimensions.IDENTIFIER.PADDING.BOTTOM,
      idFontSize
    );
    const SCALED_IDENTIFIER_MARGIN_X = scalePixelsToFontSize(ITEM_CARD_IDENTIFIER_MARGIN_X, idFontSize);
    const SCALED_IDENTIFIER_MARGIN_Y = scalePixelsToFontSize(ITEM_CARD_IDENTIFIER_MARGIN_Y, idFontSize);

    const lineHeight = Math.floor(idFontSize * IDENTIFIER_LINE_HEIGHT);

    return {
      width:
        calcWordWidth(identifier, idFontSize, widthMap) +
        2 * SCALED_INLINE_IDENTIFIER_PADDING_X +
        SCALED_IDENTIFIER_MARGIN_X,
      height:
        lineHeight +
        SCALED_INLINE_IDENTIFIER_PADDING_TOP +
        SCALED_INLINE_IDENTIFIER_PADDING_BOTTOM +
        SCALED_IDENTIFIER_MARGIN_Y * 2
    };
  }
};

export const calcTableIdentifierSize = (identifier, isAvatar, fontSize, widthMap) => {
  // TODO: link the constants to the table avatar styles (scss)
  // now they are the same as the item card
  if (isAvatar) {
    const SCALED_INLINE_AVATAR_SIZE = scalePixelsToFontSize(ITEM_CARD_INLINE_AVATAR_SIZE, fontSize);
    const SCALED_TABLE_CELL_AVATAR_MARGIN_TOP = scalePixelsToFontSize(TABLE_CELL_AVATAR_MARGIN_TOP, fontSize);
    const SCALED_TABLE_CELL_AVATAR_MARGIN_BOTTOM = scalePixelsToFontSize(TABLE_CELL_AVATAR_MARGIN_BOTTOM, fontSize);
    const SCALED_TABLE_CELL_AVATAR_MARGIN_LEFT = scalePixelsToFontSize(TABLE_CELL_AVATAR_MARGIN_LEFT, fontSize);
    return {
      width: SCALED_INLINE_AVATAR_SIZE + SCALED_TABLE_CELL_AVATAR_MARGIN_LEFT,
      height: SCALED_INLINE_AVATAR_SIZE + SCALED_TABLE_CELL_AVATAR_MARGIN_TOP + SCALED_TABLE_CELL_AVATAR_MARGIN_BOTTOM
    };
  } else {
    const idFontSize = scalePixelsToFontSize(Dimensions.IDENTIFIER.FONT_SIZE, fontSize);
    const SCALED_PADDING_TOP = scalePixelsToFontSize(Dimensions.IDENTIFIER.PADDING.TOP, idFontSize);
    const SCALED_PADDING_BOTTOM = scalePixelsToFontSize(Dimensions.IDENTIFIER.PADDING.BOTTOM, idFontSize);
    const SCALED_PADDING_X = scalePixelsToFontSize(Dimensions.IDENTIFIER.PADDING.LEFT, idFontSize);
    const SCALED_MARGIN_X = scalePixelsToFontSize(TABLE_IDENTIFIER_SLUG_MARGIN_X, idFontSize);
    const SCALED_MARGIN_TOP = scalePixelsToFontSize(TABLE_IDENTIFIER_SLUG_MARGIN_TOP, idFontSize);
    const SCALED_MARGIN_BOTTOM = scalePixelsToFontSize(TABLE_IDENTIFIER_SLUG_MARGIN_BOTTOM, idFontSize);
    const SCALED_MIN_WIDTH = scalePixelsToFontSize(TABLE_IDENTIFIER_SLUG_MIN_WIDTH, idFontSize);

    const lineHeight = Math.floor(idFontSize * IDENTIFIER_LINE_HEIGHT);

    return {
      width:
        Math.max(calcWordWidth(identifier, idFontSize, widthMap) + 2 * SCALED_PADDING_X, SCALED_MIN_WIDTH) +
        SCALED_MARGIN_X,
      height: lineHeight + SCALED_PADDING_TOP + SCALED_PADDING_BOTTOM + SCALED_MARGIN_TOP + SCALED_MARGIN_BOTTOM
    };
  }
};

export const calcSingleLineTextSize = (text, fontSize, widthMap) => {
  const SCALED_TITLE_PADDING = scalePixelsToFontSize(TEXT_TITLE_PADDING, fontSize);

  if (!text) {
    return createFromJS.TextSizeRecord({});
  }
  const lineHeight = Math.floor(fontSize * LINE_HEIGHT_RATIO);
  return createFromJS.TextSizeRecord({
    text,
    size: {
      width: calcWordWidth(text, fontSize, widthMap),
      height: lineHeight + 2 * SCALED_TITLE_PADDING
    }
  });
};

// Note: This is using the average char width which is not that accurate
export const trimTextToWidthOld = (t, fontSize, widthMap, maxWidth, suffix = " ...") => {
  const text = `${t}`;
  const line = calcSingleLineTextSize(text, fontSize, widthMap);
  const maxLineLength = maxWidth / AVERAGE_CHAR_PIXEL_WIDTH;
  if (line.size.width > maxWidth) {
    return text.slice(0, maxLineLength).concat(suffix);
  }
  return text;
};

export const trimTextToWidth = (text, inFontSize, widthMap, maxWidth, suffix = " ...") => {
  const fontSize = Math.round(inFontSize);

  // Note: .split is a tricky way of testing
  // whether the text is a string.
  if (!text || !text.split) {
    return text;
  }

  const charArr = text.split("");
  const charCount = charArr.length;
  let currentWidth = 0;
  let result = "";
  for (let i = 0; i < charCount && currentWidth < maxWidth; i++) {
    const c = charArr[i];
    if (isCharPrintable(c)) {
      const width = widthOfCharacter(c, fontSize, widthMap);
      currentWidth += width;
      result += c;
    }
  }
  if (currentWidth > maxWidth) {
    result = result.slice(0, -1);
  }

  return result.concat(suffix);
};

/**
 *
 * @param {string} c
 * @param {number} fontSize
 * @param {Map} widthMap
 */
const widthOfCharacter = (c, fontSize, widthMap) => {
  return widthMap.get(c) ? (widthMap.get(c) * fontSize) / 13 : fontSize;
};

/**
 *
 * @param {string} word
 * @param {number} fontSize
 * @param {Map} widthMap
 */
export const calcWordWidth = (word, inFontSize, widthMap) => {
  const fontSize = Math.round(inFontSize);

  // Note: word.split is a tricky way of testing
  // whether the word is a string.
  if (!word || !word.split) {
    return 0;
  }
  const w = _.reduce(
    word.split(""),
    (sum, c) => {
      if (!isCharPrintable(c)) {
        return sum;
      }
      const w = widthOfCharacter(c, fontSize, widthMap);
      return sum + w;
    },
    0
  );
  return w;
};

/**
 * For the case of allowing multiple spaces.
 * Handling the spaces at the beginning
 * @param {string[]} arr
 */
const turnBeginEmptiesToSpace = arr => {
  let hasReachLetter = false;
  return arr.map(w => (!hasReachLetter && w === "" ? " " : w));
};

/**
 *
 * @param {String} text
 * @param {number} fontSize
 * @param {Map} widthMap
 * @param {number} maxWidth
 */
export const calcTextSize = (text, inFontSize, widthMap, maxWidth, allowMultiSpaces = false, lineLimit = null) => {
  const fontSize = Math.round(inFontSize);

  const SCALED_TITLE_PADDING = scalePixelsToFontSize(TEXT_TITLE_PADDING, fontSize);

  const spaceWidth = widthOfCharacter(" ", fontSize, widthMap);

  if (!text) {
    return createFromJS.TextMetricsRecord({});
  }

  // TODO: In future, we might want to look at applying different rules for
  // Chinese/Japanese/Korean languages, where the newline wrapping is not based
  // on words but on characters
  // We could detect certain characters using a unicode range like this:
  // string.match(/[\u3400-\u30FF]/);
  // The ranges that matter for CJK are:
  // U+3040 - U+30FF: hiragana and katakana (Japanese only)
  // U+3400 - U+4DBF: CJK unified ideographs extension A (Chinese, Japanese, and Korean)
  // U+4E00 - U+9FFF: CJK unified ideographs (Chinese, Japanese, and Korean)
  // U+F900 - U+FAFF: CJK compatibility ideographs (Chinese, Japanese, and Korean)
  // U+FF66 - U+FF9F: half-width katakana (Japanese only)

  // let safeText = _.deburr(text);
  let origLines = text.split(/[\n\r]/);

  let wordsPerLine = _.map(origLines, l => {
    let arr = [];
    if (allowMultiSpaces) {
      arr = turnBeginEmptiesToSpace(l.split(" "));
      // .map(w => (w === "" ? " " : w));
    } else {
      arr = l.match(/\S+/g);
    }
    return arr && arr.length ? arr : [];
  });

  let lines = [],
    line = "",
    lineLen = 0,
    maxLineWidth = 0,
    lineCount = 0;

  wordsPerLine.forEach((words, lineIndex) => {
    let wordWidths = words.map(w => {
      return calcWordWidth(w, fontSize, widthMap);
    });

    let wordCount = words.length,
      i = 0;

    while (i < wordCount) {
      const useSpaceWidth = lineLen > 0 ? spaceWidth : 0;
      // Can we add it to the existing line?
      if (lineLen + useSpaceWidth + wordWidths[i] < maxWidth) {
        line += words[i] + " ";
        lineLen += useSpaceWidth + wordWidths[i];
        i++;
      } else if (lineLen === 0) {
        // Handle special case - the word itself is longer than our allowed width, so we just have to split
        // it somewhere...
        let word = words[i],
          wordLen = word.length;
        let fragmentWidth = 0;
        let fragmentStart = 0;
        for (let j = 0; j < wordLen; j++) {
          // Add characters until we get larger than our width
          const charWidth = widthOfCharacter(word[j], fontSize, widthMap);
          // If this character would clear our maxWidth, then use the line up until this
          // character and start again on this one
          if (fragmentWidth + charWidth > maxWidth) {
            // Add a line from fragmentStart up until j-1
            lines.push(word.slice(fragmentStart, j));
            lineCount++;
            maxLineWidth = Math.max(maxLineWidth, fragmentWidth);
            // And now reset for the next word: note the width is about to be increased
            fragmentStart = j;
            fragmentWidth = 0;
          }
          // Increase the width of our fragment
          fragmentWidth += charWidth;
        }
        // Finally, outside the loop we need to commit the final line section
        if (fragmentWidth > 0) {
          lines.push(word.slice(fragmentStart));
          lineCount++;
          maxLineWidth = Math.max(maxLineWidth, fragmentWidth);
        }
        i++;
      } else {
        // We can't add it to the current line...
        // So save the current line, and then create a new one.
        lines.push(line);
        lineCount++;
        maxLineWidth = Math.max(maxLineWidth, lineLen);
        // And reset the line for next time
        line = "";
        lineLen = 0;
        // Note that we don't increment i, so we will be testing the same word next time.
      }
    }

    // Now that we have iterated through all words in this line, complete the line
    if (lineLen > 0) {
      lines.push(line);
      lineCount++;
      if (lineLen > maxLineWidth) {
        maxLineWidth = lineLen;
      }
      // And reset the line for next time
      line = "";
      lineLen = 0;
    } else if (wordCount === 0 && lineIndex < wordsPerLine.length - 1) {
      lines.push("");
      lineCount++;
    }
  });

  // if lineLimit is set, trim it down to size
  if (!_.isNil(lineLimit) && lineCount > lineLimit) {
    lineCount = lineLimit;
    lines.length = lineLimit;
    if (lineCount === 0) {
      maxLineWidth = 0;
    }
  }

  const lineHeight = Math.floor(fontSize * LINE_HEIGHT_RATIO);

  let output = createFromJS.TextMetricsRecord({
    lines: List(lines),
    size: {
      width: Math.ceil(maxLineWidth),
      height: lineCount ? Math.ceil(lineCount * lineHeight + 2 * SCALED_TITLE_PADDING) : 0
    }
  });

  return output;
};
